// components/LocationProvider.tsx
import {
  locationAtom,
  locationPermissionAtom,
  locationFallbackAtom,
  type LocationError
} from "@/atoms/recommendationAtom";
import { NAGOYA_STATION_FALLBACK } from "@/constants/location";
import { useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { toast } from "sonner";

const LocationProvider = () => {
  const setLocation = useSetAtom(locationAtom);
  const setLocationPermission = useSetAtom(locationPermissionAtom);
  const setLocationFallback = useSetAtom(locationFallbackAtom);

  const checkPermissionStatus = useCallback(async () => {
    try {
      if (!navigator.geolocation) {
        setLocationPermission({
          state: "unavailable",
          error: {
            code: 0,
            message: "位置情報がサポートされていません",
            type: "NOT_SUPPORTED"
          },
          isLocationAvailable: false
        });
        // フォールバック処理
        setLocation({
          latitude: NAGOYA_STATION_FALLBACK.latitude,
          longitude: NAGOYA_STATION_FALLBACK.longitude
        });
        setLocationFallback({
          isUsingFallback: true,
          fallbackLocation: NAGOYA_STATION_FALLBACK
        });
        return;
      }

      // Permissions APIが利用可能な場合
      if (navigator.permissions) {
        try {
          const permission = await navigator.permissions.query({
            name: "geolocation" as PermissionName
          });

          if (permission.state === "granted") {
            setLocationPermission({
              state: "granted",
              error: null,
              isLocationAvailable: true
            });
            getCurrentLocation();
          } else if (permission.state === "denied") {
            setLocationPermission({
              state: "denied",
              error: {
                code: 1,
                message: "位置情報の利用が拒否されています",
                type: "PERMISSION_DENIED"
              },
              isLocationAvailable: false
            });
            // フォールバック処理
            setLocation({
              latitude: NAGOYA_STATION_FALLBACK.latitude,
              longitude: NAGOYA_STATION_FALLBACK.longitude
            });
            setLocationFallback({
              isUsingFallback: true,
              fallbackLocation: NAGOYA_STATION_FALLBACK
            });
          } else {
            setLocationPermission({
              state: "prompt",
              error: null,
              isLocationAvailable: false
            });
            getCurrentLocation();
          }
        } catch (error) {
          // Permissions APIが失敗した場合は直接位置情報を試行
          getCurrentLocation();
        }
      } else {
        // Permissions APIが利用できない場合は直接位置情報を試行
        getCurrentLocation();
      }
    } catch (error) {
      console.error("Permission check failed:", error);
      getCurrentLocation();
    }
  }, [setLocationPermission, setLocation, setLocationFallback]);

  const getCurrentLocation = useCallback(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setLocationPermission({
          state: "granted",
          error: null,
          isLocationAvailable: true
        });
        // フォールバック状態をクリア
        setLocationFallback({
          isUsingFallback: false,
          fallbackLocation: null
        });
        toast.success("位置情報を取得しました");
      },
      (error) => {
        console.error("Error obtaining location:", error);

        let locationError: LocationError;

        switch (error.code) {
          case error.PERMISSION_DENIED:
            locationError = {
              code: error.code,
              message: "位置情報の利用が拒否されました",
              type: "PERMISSION_DENIED"
            };
            setLocationPermission({
              state: "denied",
              error: locationError,
              isLocationAvailable: false
            });
            break;
          case error.POSITION_UNAVAILABLE:
            locationError = {
              code: error.code,
              message: "位置情報を取得できませんでした",
              type: "POSITION_UNAVAILABLE"
            };
            setLocationPermission({
              state: "unavailable",
              error: locationError,
              isLocationAvailable: false
            });
            break;
          case error.TIMEOUT:
            locationError = {
              code: error.code,
              message: "位置情報の取得がタイムアウトしました",
              type: "TIMEOUT"
            };
            setLocationPermission({
              state: "unavailable",
              error: locationError,
              isLocationAvailable: false
            });
            break;
          default:
            locationError = {
              code: error.code,
              message: "位置情報の取得に失敗しました",
              type: "UNKNOWN"
            };
            setLocationPermission({
              state: "unavailable",
              error: locationError,
              isLocationAvailable: false
            });
        }

        // エラータイプに関わらずフォールバック処理
        setLocation({
          latitude: NAGOYA_STATION_FALLBACK.latitude,
          longitude: NAGOYA_STATION_FALLBACK.longitude
        });
        setLocationFallback({
          isUsingFallback: true,
          fallbackLocation: NAGOYA_STATION_FALLBACK
        });

        toast.error(locationError.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5分間キャッシュ
      }
    );
  }, [setLocation, setLocationPermission, setLocationFallback]);

  const requestLocationPermission = useCallback(() => {
    setLocationPermission((prev) => ({
      ...prev,
      state: "checking"
    }));
    getCurrentLocation();
  }, [getCurrentLocation, setLocationPermission]);

  useEffect(() => {
    checkPermissionStatus();
  }, [checkPermissionStatus]);

  // 位置情報要求関数をグローバルに公開
  useEffect(() => {
    (window as any).requestLocationPermission = requestLocationPermission;

    return () => {
      delete (window as any).requestLocationPermission;
    };
  }, [requestLocationPermission]);

  return null; // 描画不要のコンポーネント
};

export default LocationProvider;
